/**
 * 素材アップロードのトースト (成功 / 失敗) を組み立てる。SVG (レイヤー素材) の入稿エラーは
 * error_code で文言を出し分け、差分画像は platform admin にだけ添える
 * (docs/features/motion-graphics-mode-plan.md §5.2・§6.2)。成功時の warnings はトーストにせず、
 * 素材カードと選択済み素材パネルのレイヤー一覧に出す。
 */
import { useCallback } from 'react';
import { useToast } from '../../hooks/useToast';
import { useUser } from '../auth/useUser';
import { extractLayeredIntakeError } from '../motion/layeredIntakeErrors';
import { LayeredIntakeErrorToast } from '../motion/LayeredIntakeErrorToast';
import { layerCountLabel, layerStackFromMaterial } from '../motion/layerStack';

/** 成功トーストの文言。レイヤー素材ならレイヤー数を添える */
export function uploadSuccessMessage(result: unknown, folderLabel: string): string {
  const stack = layerStackFromMaterial(result);
  if (stack) return `${folderLabel}にレイヤー素材を作成しました (${layerCountLabel(stack)})`;
  return `${folderLabel}に素材をアップロードしました`;
}

export function useMaterialUploadFeedback() {
  const toast = useToast();
  const { isPlatformOrg, isAdmin, hasPlatformAdminMembership } = useUser();
  const isPlatformAdmin = (isPlatformOrg && isAdmin) || hasPlatformAdminMembership;

  const showUploadError = useCallback((error: unknown) => {
    const layered = extractLayeredIntakeError(error);
    if (layered) {
      toast.error(<LayeredIntakeErrorToast error={layered} showDiffImage={isPlatformAdmin} />);
      return;
    }
    const e = error as { response?: { data?: { error?: string } }; message?: string } | null;
    const message = e?.response?.data?.error || e?.message || 'アップロードに失敗しました';
    toast.error(`アップロードエラー: ${message}`);
  }, [toast, isPlatformAdmin]);

  return { showUploadError };
}
